import { useState, useEffect, useCallback } from 'react';
import { Card, Table, Tag, Typography, Space, Button, Modal, Spin, Empty, Statistic, Row, Col, message } from 'antd';
import {
  SearchOutlined, CodeOutlined, TeamOutlined, FileWordOutlined,
  EyeOutlined, FolderOutlined,
} from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import api from '../../services/api';
import { generateWordFromTask, generateWordFromText, getFileDownloadUrl } from '../../services/teamCollabApi';
import './HistoryPage.css';

const { Text, Title } = Typography;

const MODE_MAP: Record<string, { label: string; color: string }> = {
  design: { label: '🎨 设计', color: '#1677ff' },
  development: { label: '💻 开发', color: '#52c41a' },
  testing: { label: '🧪 测试', color: '#fa8c16' },
  deployment: { label: '🚀 部署', color: '#722ed1' },
  text: { label: '📝 文本', color: '#1677ff' },
  word: { label: '📄 Word', color: '#2b579a' },
  excel: { label: '📊 Excel', color: '#217346' },
};

function formatTokens(n: number) {
  if (!n) return '-';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

function formatTime(ms: number) {
  if (!ms) return '-';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}min`;
}

export default function HistoryPage() {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [keyword, setKeyword] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState<string | undefined>(undefined);
  const [projectList, setProjectList] = useState<any[]>([]);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailItem, setDetailItem] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [wordExporting, setWordExporting] = useState(false);

  // Word导出处理
  useEffect(() => {
    api.get('/projects').then(r => {
      setProjectList(r.data || []);
    }).catch(() => {});
  }, []);

  const loadData = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const r = await api.get('/history', {
        params: {
          page: p,
          pageSize: 20,
          keyword: keyword || undefined,
          projectId: selectedProjectId || undefined,
        },
      });
      setData(r.data?.items || []);
      setTotal(r.data?.total || 0);
    } catch { setData([]); }
    setLoading(false);
  }, [keyword, selectedProjectId]);

  useEffect(() => { loadData(page); }, [page]);
  // 关键词或项目变化时重置到第一页
  useEffect(() => { setPage(1); loadData(1); }, [keyword, selectedProjectId]);

  const handleSearch = () => { setPage(1); loadData(1); };
  const handleClearFilters = () => {
    setKeyword('');
    setSelectedProjectId(undefined);
    setPage(1);
  };

  const showDetail = async (item: any) => {
    setDetailItem(item);
    setDetailOpen(true);
    if (item.raw?.id && item.raw?.type) {
      setDetailLoading(true);
      try {
        const r = await api.get(`/history/${item.raw.id}`, { params: { type: item.raw.type } });
        setDetailItem({ ...item, detail: r.data });
      } catch { setDetailItem(item); }
      setDetailLoading(false);
    }
  };

  const handleExportWord = async () => {
    if (!detailItem) return;
    setWordExporting(true);
    try {
      let res;
      if (detailItem.raw?.type === 'team_collab' && detailItem.raw?.id) {
        res = await generateWordFromTask(detailItem.raw.id);
      } else {
        const title = detailItem.title || 'AI任务结果';
        const content = detailItem.detail?.result || detailItem.detail?.summary || detailItem.summary || '';
        if (!content) { message.warning('无内容可导出'); setWordExporting(false); return; }
        res = await generateWordFromText(title, content);
      }
      if (res.success && res.resultFileId) {
        message.success('Word文档已生成，正在下载...');
        let token = '';
        try { const s = JSON.parse(localStorage.getItem('auth-storage') || '{}'); token = s?.state?.token || ''; } catch {}
        const fetchRes = await fetch(getFileDownloadUrl(res.resultFileId), { headers: token ? { Authorization: `Bearer ${token}` } : {} });
        if (fetchRes.ok) {
          const blob = await fetchRes.blob();
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a'); a.href = url; a.download = res.resultFileName || 'export.doc'; a.click();
          URL.revokeObjectURL(url);
        }
      } else { message.error(res.error || '导出失败'); }
    } catch (e: any) { message.error('导出失败: ' + (e.message || '网络错误')); }
    finally { setWordExporting(false); }
  };

  const columns = [
    {
      title: '来源', dataIndex: 'type', width: 90,
      render: (v: string) => v === 'ai_flow'
        ? <Tag color="purple" icon={<CodeOutlined />}>AI全流程</Tag>
        : <Tag color="orange" icon={<TeamOutlined />}>团队协作</Tag>,
    },
    {
      title: '模式/类型', dataIndex: 'mode', width: 100,
      render: (v: string) => {
        const m = MODE_MAP[v];
        return m ? <Tag color={m.color} style={{ borderRadius: 6 }}>{m.label}</Tag> : <Tag>{v || '-'}</Tag>;
      },
    },
    {
      title: '所属项目', dataIndex: 'projectName', width: 160,
      render: (v: string, r: any) => {
        if (r.type !== 'ai_flow') return <Text type="secondary">-</Text>;
        return v
          ? <div style={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}><Tag icon={<FolderOutlined />} color="blue">{v}</Tag></div>
          : <Text type="secondary">未关联</Text>;
      },
    },
    {
      title: '任务内容', dataIndex: 'title', ellipsis: true,
      render: (v: string, r: any) => (
        <div>
          <Text ellipsis={{ tooltip: v }}>{v}</Text>
          {r.summary && <div style={{ marginTop: 4 }}><Text type="secondary" ellipsis>{r.summary}</Text></div>}
        </div>
      ),
    },
    {
      title: '状态', dataIndex: 'status', width: 70,
      render: (v: string) => (
        <Tag color={v === 'completed' ? 'green' : v === 'failed' ? 'red' : 'default'}>
          {v === 'completed' ? '✅' : v === 'failed' ? '❌' : v}
        </Tag>
      ),
    },
    {
      title: 'Token', dataIndex: 'totalTokens', width: 70, align: 'right' as const,
      render: (v: number) => <Text>{formatTokens(v)}</Text>,
    },
    {
      title: '耗时', dataIndex: 'elapsedMs', width: 70,
      render: (v: number) => <Text>{formatTime(v)}</Text>,
    },
    {
      title: '时间', dataIndex: 'createdAt', width: 140,
      render: (v: string) => <Text>{new Date(v).toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}</Text>,
      sorter: (a: any, b: any) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      defaultSortOrder: 'descend' as const,
    },
    {
      title: '', width: 50,
      render: (_: any, r: any) => (
        <Button size="small" type="link" icon={<EyeOutlined />} onClick={() => showDetail(r)} />
      ),
    },
  ];

  const totalTokens = data.reduce((s, r) => s + (r.totalTokens || 0), 0);

  return (
    <div className="history-page" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div className="history-page-header" style={{ flexShrink: 0, background: '#ffffff', border: '1px solid #bae6fd' }}>
        <div className="history-hero-copy">
          <span className="history-hero-icon">📜</span>
          <div>
            <Title level={3} style={{ margin: 0, color: '#000000', fontWeight: 950 }}>历史记录</Title>
            <Text style={{ color: '#0f172a', fontSize: 15, fontWeight: 850 }}>查询 AI全流程 & 团队协作 所有历史任务</Text>
          </div>
        </div>
      </div>

      <div className="history-page-filter" style={{ display: 'flex', gap: 14, alignItems: 'center', flexShrink: 0, flexWrap: 'wrap', background: 'transparent', border: 0, boxShadow: 'none' }}>
        <div className="history-filter-control history-project-filter">
          <span className="history-filter-label">项目</span>
          <select
            value={selectedProjectId || ''}
            onChange={(e) => setSelectedProjectId(e.target.value || undefined)}
            aria-label="筛选项目"
          >
            <option value="">全部项目</option>
            {projectList.map((p: any) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <div className="history-filter-control history-search-filter">
          <span className="history-filter-label">搜索</span>
          <input
            placeholder="搜索任务内容..."
            value={keyword}
            onChange={e => setKeyword(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleSearch(); }}
            aria-label="搜索任务内容"
          />
          {keyword && <button type="button" className="history-filter-clear" onClick={() => setKeyword('')}>清空</button>}
        </div>
        <button type="button" className="history-search-button" onClick={handleSearch}><SearchOutlined /> 搜索</button>
        {(keyword || selectedProjectId) && <button type="button" className="history-reset-button" onClick={handleClearFilters}>重置</button>}
        <div style={{ flex: 1 }} />
        <Row gutter={16}>
          <Col><Statistic title="记录数" value={total} valueStyle={{ fontSize: 22, color: '#1677ff', fontWeight: 900 }} /></Col>
          <Col><Statistic title="Token合计" value={formatTokens(totalTokens)} valueStyle={{ fontSize: 22, color: '#fa8c16', fontWeight: 900 }} /></Col>
        </Row>
      </div>

      <div className="history-page-body" style={{ flex: 1, overflow: 'auto' }}>
        {loading ? <Spin style={{ display: 'block', margin: '60px auto' }} /> :
          data.length === 0 ? <Empty description="暂无历史记录" style={{ marginTop: 60 }} /> :
            <Table
              size="middle"
              dataSource={data}
              rowKey="id"
              columns={columns}
              loading={loading}
              pagination={{ current: page, total, pageSize: 20, onChange: setPage, showSizeChanger: false, showTotal: (t: number) => `共 ${t} 条` }}
            />}
      </div>

      <Modal
        title="任务详情"
        open={detailOpen}
        onCancel={() => setDetailOpen(false)}
        width={960}
        footer={null}
        style={{ top: 30 }}
        className="history-detail-modal"
      >
        {detailLoading ? <Spin style={{ display: 'block', margin: '60px auto' }} /> : detailItem ? (
          <>
            <Card size="small" style={{ marginBottom: 12 }}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <div>
                  {detailItem.type === 'ai_flow'
                    ? <Tag color="purple" icon={<CodeOutlined />}>AI全流程</Tag>
                    : <Tag color="orange" icon={<TeamOutlined />}>团队协作</Tag>}
                  {detailItem.mode && <Tag color={MODE_MAP[detailItem.mode]?.color}>{MODE_MAP[detailItem.mode]?.label || detailItem.mode}</Tag>}
                  {detailItem.projectName && <Tag color="blue" icon={<FolderOutlined />}>{detailItem.projectName}</Tag>}
                  <Tag color={detailItem.status === 'completed' ? 'green' : 'red'}>{detailItem.status === 'completed' ? '已完成' : detailItem.status}</Tag>
                </div>
                <Text strong style={{ fontSize: 18, lineHeight: 1.7, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{detailItem.title}</Text>
                <Row gutter={16}>
                  <Col span={8}><Text type="secondary">Token: <Text strong>{formatTokens(detailItem.totalTokens)}</Text></Text></Col>
                  <Col span={8}><Text type="secondary">耗时: <Text strong>{formatTime(detailItem.elapsedMs)}</Text></Text></Col>
                  <Col span={8}><Text type="secondary">时间: {new Date(detailItem.createdAt).toLocaleString('zh-CN')}</Text></Col>
                </Row>
                {detailItem.resultFileName && (
                  <div><Text type="secondary">生成文件: </Text><Tag>{detailItem.resultFileName}</Tag></div>
                )}
              </Space>
            </Card>

            {/* 结果/摘要 - 使用完整数据 */}
            {(() => {
              const fullContent = detailItem.detail?.result || detailItem.detail?.summary || detailItem.summary || '';
              if (!fullContent) return null;
              return (
                <Card size="small" title={<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>📄 执行结果</span>
                  <Button size="small" type="primary" ghost icon={<FileWordOutlined />} onClick={handleExportWord} loading={wordExporting}>导出Word</Button>
                </div>} style={{ marginBottom: 12 }}>
                  <div className="history-detail-result" style={{ maxHeight: 650, overflow: 'auto', borderRadius: 12, padding: 18 }}>
                    <div className="md-content"><ReactMarkdown remarkPlugins={[remarkGfm]}>{fullContent}</ReactMarkdown></div>
                  </div>
                </Card>
              );
            })()}

            {detailItem.thinkingContent && (
              <Card size="small" title="🧠 AI思考过程" style={{ marginBottom: 12 }}>
                <div className="history-detail-thinking" style={{ maxHeight: 460, overflow: 'auto', borderRadius: 12, padding: 18 }}>
                  <div className="md-content"><ReactMarkdown remarkPlugins={[remarkGfm]}>{detailItem.thinkingContent}</ReactMarkdown></div>
                </div>
              </Card>
            )}

            {detailItem.detail?.messages && (
              <Card size="small" title="💬 对话记录">
                <div style={{ maxHeight: 500, overflow: 'auto' }}>
                  {detailItem.detail.messages.map((m: any, i: number) => (
                    <div key={i} className="history-detail-message" style={{ padding: '10px 0' }}>
                      <Tag color={m.role === 'user' ? 'blue' : m.role === 'assistant' ? 'green' : 'default'}>
                        {m.role === 'user' ? '👤 用户' : m.role === 'assistant' ? '🤖 AI' : m.role}
                      </Tag>
                      {m.toolName && <Tag color="purple">🔧 {m.toolName}</Tag>}
                      <Text style={{ display: 'block', marginTop: 8, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                        {m.content}
                      </Text>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </>
        ) : <Empty />}
      </Modal>
    </div>
  );
}
